"use client";

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { defaultLocale, locales, type Locale } from "./config";
import { viMessages } from "./messages.vi";
import { enMessages } from "./messages.en";

type Messages = typeof viMessages | typeof enMessages;

type I18nContextValue = {
  locale: Locale;
  messages: Messages;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function getMessages(locale: Locale): Messages {
  switch (locale) {
    case "en":
      return enMessages;
    case "vi":
    default:
      return viMessages;
  }
}

function translate(messages: Messages, key: string): string {
  const parts = key.split(".");
  let current: any = messages;

  for (const part of parts) {
    if (current && typeof current === "object" && part in current) {
      current = current[part];
    } else {
      return key;
    }
  }

  return typeof current === "string" ? current : key;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const stored = window.localStorage.getItem("locale") as Locale | null;
    if (stored && (locales as readonly string[]).includes(stored)) {
      setLocaleState(stored as Locale);
      document.documentElement.lang = stored;
    } else {
      document.documentElement.lang = defaultLocale;
    }
  }, []);

  const setLocale = (next: Locale) => {
    setLocaleState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("locale", next);
      document.documentElement.lang = next;
    }
  };

  const messages = useMemo(() => getMessages(locale), [locale]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      messages,
      setLocale,
      t: (key: string) => translate(messages, key),
    }),
    [locale, messages]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used within I18nProvider");
  }
  return ctx;
}

